/**
 * WebAuthn browser helpers — encode/decode between ArrayBuffer and base64url.
 */

export function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

/**
 * Start a WebAuthn registration ceremony.
 * @param {Object} options - PublicKeyCredentialCreationOptions from the server (JSON-decoded)
 * @returns {Object} - credential response ready to POST to /register/verify
 */
export async function startRegistration(options) {
  // Decode base64url fields to ArrayBuffer
  const publicKey = {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    user: {
      ...options.user,
      id: base64urlToBuffer(options.user.id),
    },
    excludeCredentials: (options.excludeCredentials || []).map(c => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  const credential = await navigator.credentials.create({ publicKey });

  return {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
    response: {
      clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
      attestationObject: bufferToBase64url(credential.response.attestationObject),
      transports: credential.response.getTransports ? credential.response.getTransports() : [],
    },
  };
}

/**
 * Start a WebAuthn authentication ceremony.
 * @param {Object} options - PublicKeyCredentialRequestOptions from the server (JSON-decoded)
 * @returns {Object} - assertion response ready to POST to /authenticate/verify
 */
export async function startAuthentication(options) {
  const publicKey = {
    ...options,
    challenge: base64urlToBuffer(options.challenge),
    allowCredentials: (options.allowCredentials || []).map(c => ({
      ...c,
      id: base64urlToBuffer(c.id),
    })),
  };

  const assertion = await navigator.credentials.get({ publicKey });

  return {
    id: assertion.id,
    rawId: bufferToBase64url(assertion.rawId),
    type: assertion.type,
    response: {
      clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
      authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
      signature: bufferToBase64url(assertion.response.signature),
      userHandle: assertion.response.userHandle
        ? bufferToBase64url(assertion.response.userHandle)
        : null,
    },
  };
}

/**
 * Check if WebAuthn is supported in this browser.
 */
export function isWebAuthnSupported() {
  return !!(navigator.credentials && navigator.credentials.create && navigator.credentials.get);
}
